import { useFormik } from "formik";
import * as Yup from "yup";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import { useMemo } from "react";
import SelectField from "../../ui/form/SelectField";
import toast from "react-hot-toast";

const OrderReport = ({ order, closeModal, fetchData }) => {
  const allStatusOptions = ["Dispatched", "Requested to Cancel"];
  const hasDispatchedOrCancel = (order.reports || []).some((report) =>
    ["Dispatched", "Requested to Cancel"].includes(report.status)
  );
  const filteredStatusOptions = hasDispatchedOrCancel ? [] : allStatusOptions;

  const reportedStatuses = useMemo(() => {
    return (order.reports || []).map((report) => report.status);
  }, [order.reports]);

  const statusOptions = useMemo(() => {
    return allStatusOptions.filter(
      (status) => !reportedStatuses.includes(status)
    );
  }, [allStatusOptions, reportedStatuses]);

  const {
    error,
    isLoading: loading,
    post,
    serverErrors,
  } = usePost(apiRequest.updateOrder);

  // Initialize formik first
  const formik = useFormik({
    initialValues: {
      status: "",
      remarks: "",
      productUpdates: order.products.map((p) => {
        const brandName = p.product?.brand?.name ?? "";
        const productTitle = p.product?.title ?? "";
        const title = [brandName, productTitle].filter(Boolean).join(" ");

        return {
          title,
          productId: p.product?._id || p.product,
          imei: p.imei ?? [],
          image: null,
        };
      }),
    },
    validationSchema: Yup.object().shape({
      status: Yup.string()
        .oneOf(statusOptions, "Invalid status")
        .required("Status is required"),
      remarks: Yup.string().optional(),
      productUpdates: Yup.array().when("status", (status, schema) => {
        if (status === "Dispatched") {
          return schema.of(
            Yup.object().shape({
              productId: Yup.string().required("Product ID is required"),

              imei: Yup.array()
                .of(Yup.string().required("IMEI is required"))
                .test(
                  "imei-count",
                  "Invalid number of IMEIs",
                  function (value) {
                    const product = order.products.find(
                      (p) => p.product._id === this.parent.productId
                    );
                    const category = product?.product?.category?.name;
                    const validCount = category === "Mobile Phone" ? 2 : 1;
                    return value?.filter(Boolean).length === validCount;
                  }
                ),

              image: Yup.mixed().when("$status", {
                is: "Dispatched",
                then: (schema) =>
                  schema
                    .required("Image is required")
                    .test(
                      "file-type",
                      "Invalid file",
                      (value) =>
                        value instanceof File || typeof value === "string"
                    ),
                otherwise: (schema) => schema.notRequired(),
              }),
            })
          );
        }

        return schema.notRequired();
      }),
    }),
    onSubmit: async (values) => {
      if (values.status === "Dispatched") {
        const missingImage = values.productUpdates.some(
          (item) => !item.image || item.image === ""
        );

        if (missingImage) {
          toast.error("Please upload images for all dispatched products.");
          return;
        }
      }
      const formData = new FormData();
      formData.append("status", values.status);
      if (values.remarks) formData.append("remark", values.remarks);

      values.productUpdates.forEach((item, index) => {
        formData.append(`productUpdates[${index}][productId]`, item.productId);
        item.imei.forEach((imeiVal, i) => {
          formData.append(`productUpdates[${index}][imei][${i}]`, imeiVal);
        });
        if (item.image) {
          formData.append(`productUpdates[${index}][image]`, item.image);
        }
      });

      const res = await post({
        data: { body: formData, id: order._id },
      });

      if (res?.response) {
        closeModal();
        fetchData();
      }
    },
  });
  return (
    <div className="max-w-4xl p-5 mx-auto bg-white shadow-md rounded-lg font-sans text-[13px] text-gray-700">
      <h2 className="mb-4 text-sm font-semibold">Update Order Status</h2>

      <form onSubmit={formik.handleSubmit}>
        {/* Status */}
        <div className={!formik.values.status ? "pb-[100px]" : ""}>
          <SelectField
            name="status"
            label="Status"
            placeholder="Choose Status"
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            serverError={serverErrors?.status}
            error={formik.touched.status && formik.errors.status}
            options={filteredStatusOptions?.map((item) => ({
              value: item,
              label: item,
            }))}
          />
        </div>

        {/* Remarks for Cancellation */}
        {formik.values?.status === "Requested to Cancel" && (
          <>
            <label className="block mt-4 mb-1 font-medium">Remarks</label>
            <textarea
              name="remarks"
              rows="2"
              className="w-full p-2 border border-gray-300 rounded text-[13px]"
              value={formik.values.remarks}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </>
        )}

        {/* Product Update Fields */}
        {formik.values?.status === "Dispatched" &&
          formik.values.productUpdates.map((product, index) => {
            const matchedProduct = order?.products.find(
              (item) => item.product._id === product.productId
            );
            const brandName = matchedProduct?.product?.brand?.name || "N/A";
            const modelName = matchedProduct?.product?.modelId?.name || "N/A";
            const category = matchedProduct?.product?.category?.name || "";

            return (
              <div
                key={product.productId}
                className="p-3 mt-2 mb-3 border rounded shadow bg-emerald-100"
              >
                <div className="text-[12px] font-semibold mb-2">
                  {`${brandName} - ${modelName}`}
                </div>

                {/* IMEI fields */}
                {[0, 1].map((i) =>
                  category === "Mobile Phone" || i === 0 ? (
                    <div key={i}>
                      <label className="block text-[12px] mt-2 mb-1">
                        IMEI {i + 1}/Serial No.
                      </label>
                      <input
                        type="text"
                        name={`productUpdates.${index}.imei[${i}]`}
                        className="w-full p-1 border border-gray-300 rounded text-[13px]"
                        value={
                          formik.values.productUpdates[index].imei?.[i] || ""
                        }
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.productUpdates?.[index]?.imei?.[i] &&
                        formik.errors.productUpdates?.[index]?.imei?.[i] && (
                          <div className="text-xs text-red-600">
                            {formik.errors.productUpdates[index].imei[i]}
                          </div>
                        )}
                    </div>
                  ) : null
                )}

                {/* Image Upload */}
                <label className="block text-[12px] mt-3 mb-1">
                  Upload Image of IMEI/Serial No.{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  required={true}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `productUpdates.${index}.image`,
                      e.currentTarget.files?.[0] || null
                    )
                  }
                  onBlur={formik.handleBlur}
                  className="w-full text-[13px]"
                />
                {formik.touched.productUpdates?.[index]?.image &&
                  formik.errors.productUpdates?.[index]?.image && (
                    <div className="text-xs text-red-600">
                      {formik.errors.productUpdates[index].image}
                    </div>
                  )}
              </div>
            );
          })}

        {/* Server/Hook Errors */}
        {error && (
          <div className="mb-2 text-xs text-red-600">{error.message}</div>
        )}
        {serverErrors && (
          <div className="mb-2 text-xs text-red-600">{serverErrors}</div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={closeModal}
            className="px-3 py-1 rounded bg-gray-300 hover:bg-gray-400 text-[13px]"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-3 py-1 rounded bg-green-600 hover:bg-green-700 text-white text-[13px]"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Order"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrderReport;
